import { useCallback, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router";

import type { ClipCue } from "../../services/generated/client.js";
import { playbackFor } from "../../lib/audio.js";
import {
  forgetSeat,
  rememberSeat,
  rememberedSeat,
  type DisplaySeat,
} from "../../services/displayService.js";
import { appUrl, hasServerOrigin } from "../../services/serverOrigin.js";
import { Board } from "./Board.js";
import { JoinInvite } from "./JoinInvite.js";
import { PairPanel } from "./PairPanel.js";
import { useBoard, type BoardSeat } from "./useBoard.js";

/**
 * The television — the ten-foot board, and the audio sink.
 *
 * # A sibling route with its own root layout (ADR 0005)
 *
 * Not a variant of the player route behind a flag. This page ships as a Tizen
 * and webOS app in phase 10, and **that estimate is only true while it never
 * imports the player shell** — `eslint.config.ts` refuses `pages/party/**` from
 * this directory, and the shared vocabulary is `components/game/`, which both
 * surfaces use and neither owns.
 *
 * # It sends nothing
 *
 * A display holds a party code and a read-only stream. Every input comes from a
 * phone. The server enforces it too — `require_actor` refuses a display any
 * intent, and `watch` hands it no player topic — so this is the same rule
 * expressed in three places, which is what a rule that matters looks like.
 */
export function DisplayPage() {
  const { code } = useParams();
  const room = (code ?? "").toUpperCase();

  // A packaged app (phase 11) is loaded from a file, so there is no origin to
  // derive the server's from, and every URL on this screen needs one. Said in
  // ten-foot type rather than attempted: a socket to `ws:///rpc` fails with
  // nothing in the console, and the television shows a spinner forever — which
  // is the failure this project has now met twice.
  //
  // Split into components rather than an early return, because the hooks below
  // must not be called conditionally.
  if (!hasServerOrigin()) {
    return <NotConfigured />;
  }
  // **The two ways a television gets a seat** (phase 11c). With a room in the
  // URL this is a browser and opening the URL is the join. Without one — which
  // is every packaged app, since there is no URL bar to put a code in — it is
  // paired: the television shows a code and the host types it into their phone.
  if (room.length === 0) {
    return <PairedTelevision />;
  }
  return <Television seat={{ kind: "room", code: room }} />;
}

/**
 * A television with no room in its URL: the packaged case.
 *
 * The seat is kept in the app's own storage, so switching the set on again is
 * not a second pairing — and a seat the server no longer accepts is *forgotten*
 * rather than retried, because a television showing "can't reach the party"
 * with nobody standing at it is a dead screen until somebody power-cycles it.
 */
function PairedTelevision() {
  const [seat, setSeat] = useState<DisplaySeat | null>(() => rememberedSeat());

  const paired = useCallback((next: DisplaySeat) => {
    rememberSeat(next);
    setSeat(next);
  }, []);

  const lost = useCallback(() => {
    forgetSeat();
    setSeat(null);
  }, []);

  if (!seat) {
    return <PairPanel onPaired={paired} />;
  }
  return (
    <Television
      seat={{ kind: "paired", code: seat.code, token: seat.token }}
      onSeatLost={lost}
    />
  );
}

/** The television, once it knows where to look and which party it is showing. */
function Television({ seat, onSeatLost }: { seat: BoardSeat; onSeatLost?: () => void }) {
  const { t } = useTranslation();
  const board = useBoard(seat);
  const room = seat.code;

  // A refused seat is not a failed connection: it is a party that ended or a
  // token that was superseded, and the only way back is a fresh pairing. Done
  // in an effect rather than during render because it changes a parent's state.
  useEffect(() => {
    if (board.seatLost) {
      onSeatLost?.();
    }
  }, [board.seatLost, onSeatLost]);

  return (
    <main className="min-h-full bg-stage-950">
      {/* The display is the sink in couch mode: one television rather than six
          phones a few hundred milliseconds apart. */}
      <ClipPlayer cue={board.cue} />

      {board.status === "failed" ? (
        <Frame>
          <h1 className="text-5xl font-semibold">{t("tv.unreachable")}</h1>
          <p className="max-w-3xl text-2xl text-white/60">{board.error}</p>
          <button
            type="button"
            onClick={board.reconnect}
            className="min-h-16 rounded-2xl border-2 border-white/25 px-8 text-2xl"
          >
            {t("party.retry")}
          </button>
        </Frame>
      ) : board.live && board.board ? (
        <Board
          board={board.board}
          rules={board.rules}
          roster={board.roster}
          gameId={board.gameId}
          code={room}
          phaseEndsAt={board.phaseEndsAt}
          phaseSeconds={board.phaseSeconds}
        />
      ) : (
        // Before the first round — and between games — the screen's job is to
        // get people in. This is the only thing on it.
        <Frame>
          <JoinInvite code={room} url={joinUrl(room)} />
          <p className="text-2xl text-white/40">
            {board.status === "connecting" ? t("party.connecting") : t("tv.waitingHost")}
          </p>
        </Frame>
      )}
    </main>
  );
}

/** Centred, generously spaced — everything on a television that is not the board. */
function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-8 p-12 text-center">
      {children}
    </div>
  );
}

/**
 * This app does not know where sonolume is.
 *
 * Not a network error and not worth a "try again": nothing about retrying will
 * make an unconfigured build find a server. It says what is missing, at the size
 * everything else on this screen is read at.
 */
function NotConfigured() {
  const { t } = useTranslation();

  return (
    <Frame>
      <h1 className="text-5xl font-semibold">{t("tv.notConfigured.title")}</h1>
      <p className="max-w-3xl text-2xl text-white/60">{t("tv.notConfigured.lead")}</p>
      {/* The variable name is the one actionable thing on this screen, so it
          keeps its monospace wherever the sentence puts it. */}
      <p className="max-w-3xl text-xl text-white/40">
        <Trans
          i18nKey="tv.notConfigured.fix"
          components={{ var: <span className="font-mono" /> }}
        />
      </p>
    </Frame>
  );
}

/** Where a phone should point. The server serves the app, so it is one origin. */
function joinUrl(code: string): string {
  return appUrl(`/party/${code}`);
}

/**
 * The audio sink.
 *
 * In couch mode this screen plays the clip and the phones stay quiet — that is
 * what the layout setting decides (`Watching.device_layout`), and it is the
 * whole reason couch mode is a mode. A television has speakers pointed at the
 * room; six phones do not.
 *
 * Autoplay: a browser refuses audio no gesture asked for, and **a television has
 * no gesture** — nobody taps this page. In practice a smart-TV browser opened by
 * hand has had one, but a Tizen build (phase 10) may need the audio started from
 * the app shell. Failure is caught and dropped either way: a silent clip must
 * not take the round with it.
 */
function ClipPlayer({ cue }: { cue: ClipCue | null }) {
  const audio = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const element = audio.current;
    if (!element || !cue) {
      return;
    }
    // `load()` as well as `src`: re-assigning an identical `src` is a no-op, so
    // the second time a room hears the same track it would be silent.
    //
    // ⚠️ Phase 19b: a cue names its provider, because a party's deck unions the
    // local catalog with Spotify tracks. Only the local branch plays through
    // this element; the Spotify sink is 19d, and until it exists such a card is
    // never dealt.
    const play = playbackFor(cue.audio);
    if (play.kind !== "element") {
      return;
    }
    element.src = play.src;
    element.currentTime = 0;
    element.load();
    void element.play().catch(() => {
      // Autoplay refused, or the clip is missing.
    });

    const stop = window.setTimeout(() => {
      element.pause();
    }, cue.seconds * 1000);
    return () => {
      window.clearTimeout(stop);
    };
  }, [cue]);

  // No caption track: a music clip has no dialogue, and the board says in words
  // what is happening.
  return <audio ref={audio} preload="none" aria-hidden="true" />;
}
